import { useEffect, useState } from 'react'
import {
  Plus, Trash2, Pencil, GitBranch, AlertTriangle, ToggleLeft, ToggleRight,
  ChevronDown, ChevronUp, GripVertical,
} from 'lucide-react'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils/cn'
import { nanoid } from 'nanoid'
import type {
  FormDefinition, ConditionalRule, ConditionalAction,
  ConditionalExpression, ComparisonCondition, ConditionOperator,
  FieldDefinition, SelectOption,
} from '../api/form-builder-api'
import {
  readLooseLabel,
  OPERATOR_LABELS,
  OPERATORS_BY_TYPE,
  ACTION_LABELS,
  conditionSummary,
  ruleSummary,
  detectConflicts,
  collectFieldKeys,
  type RuleConflict,
} from './rules-section-logic'

// ─── Condition editor ─────────────────────────────────────────────────────────

function ComparisonEditor({
  condition,
  fields,
  onChange,
}: {
  condition: ComparisonCondition
  fields: FieldDefinition[]
  onChange: (updated: ComparisonCondition) => void
}) {
  const field = fields.find((f) => f.field_key === condition.field_key)
  const operators = OPERATORS_BY_TYPE[field?.field_type ?? 'text'] ?? ['equals', 'not_equals']
  const needsValue = !['is_true', 'is_false', 'is_empty', 'is_not_empty'].includes(condition.operator)
  const isOptionOp = condition.operator === 'contains_option' || condition.operator === 'does_not_contain_option'
  const isCountOp = condition.operator === 'count_at_least' || condition.operator === 'count_at_most' || condition.operator === 'count_equals'
  const isPatternOp = condition.operator === 'matches_pattern'
  const canCompareToField = needsValue && !isOptionOp && !isCountOp

  const fieldOptions = (field?.type_config?.options as SelectOption[] | undefined) ?? []

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground shrink-0">اگر</span>

      {/* Field picker */}
      <Select
        value={condition.field_key}
        onValueChange={(v) => onChange({ ...condition, field_key: v, value: undefined, compare_to_field_key: undefined })}
      >
        <SelectTrigger className="h-7 text-xs w-40">
          <SelectValue placeholder="فیلد..." />
        </SelectTrigger>
        <SelectContent>
          {fields.map((f) => (
            <SelectItem key={f.field_key} value={f.field_key}>
              {f.label.fa || f.field_key}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator */}
      <Select
        value={condition.operator}
        onValueChange={(v) => onChange({ ...condition, operator: v as ConditionOperator, value: undefined, compare_to_field_key: undefined })}
      >
        <SelectTrigger className="h-7 text-xs w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op} value={op}>{OPERATOR_LABELS[op]}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value — option picker for contains_option / does_not_contain_option */}
      {isOptionOp && (
        <Select
          value={String(condition.value ?? '')}
          onValueChange={(v) => onChange({ ...condition, value: v })}
        >
          <SelectTrigger className="h-7 text-xs w-40">
            <SelectValue placeholder="انتخاب گزینه..." />
          </SelectTrigger>
          <SelectContent>
            {fieldOptions.map((opt) => (
              <SelectItem key={opt.option_key} value={opt.option_key}>
                {opt.label.fa || opt.option_key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Value — numeric input for count_* operators */}
      {isCountOp && (
        <Input
          type="number"
          value={String(condition.value ?? '')}
          onChange={(e) => onChange({ ...condition, value: e.target.value ? Number(e.target.value) : undefined })}
          placeholder="تعداد"
          className="h-7 text-xs w-20"
          dir="ltr"
          min={0}
        />
      )}

      {/* Value — dynamic input keyed on the source field's type so the editor
          always speaks the right language: boolean → Yes/No dropdown using the
          field's authored true/false labels; single/multi_select → dropdown of
          the field's option_keys (showing labels); pattern op → ltr regex
          textbox; otherwise a free-text input. The stored value is always the
          underlying primitive (true/false/option_key/string/number) so the
          condition engine compares against the same primitives that
          submissions persist. */}
      {needsValue && !isOptionOp && !isCountOp && !isPatternOp && !condition.compare_to_field_key && (
        (() => {
          const ft = field?.field_type
          if (ft === 'boolean') {
            const trueLabel = readLooseLabel((field?.type_config as Record<string, unknown> | undefined)?.true_label) || 'بله'
            const falseLabel = readLooseLabel((field?.type_config as Record<string, unknown> | undefined)?.false_label) || 'خیر'
            const cur = condition.value === true ? 'true' : condition.value === false ? 'false' : ''
            return (
              <Select value={cur} onValueChange={(v) => onChange({ ...condition, value: v === 'true' })}>
                <SelectTrigger className="h-7 text-xs w-32"><SelectValue placeholder="مقدار..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{trueLabel}</SelectItem>
                  <SelectItem value="false">{falseLabel}</SelectItem>
                </SelectContent>
              </Select>
            )
          }
          if (ft === 'single_select' || ft === 'multi_select') {
            return (
              <Select value={String(condition.value ?? '')} onValueChange={(v) => onChange({ ...condition, value: v })}>
                <SelectTrigger className="h-7 text-xs w-40"><SelectValue placeholder="انتخاب گزینه..." /></SelectTrigger>
                <SelectContent>
                  {fieldOptions.map((opt) => (
                    <SelectItem key={opt.option_key} value={opt.option_key}>
                      {opt.label.fa || opt.option_key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          }
          if (ft === 'number') {
            return (
              <Input
                type="number"
                value={String(condition.value ?? '')}
                onChange={(e) => onChange({ ...condition, value: e.target.value === '' ? undefined : Number(e.target.value) })}
                placeholder="مقدار"
                className="h-7 text-xs w-28"
                dir="ltr"
              />
            )
          }
          if (ft === 'date' || ft === 'datetime') {
            return (
              <Input
                type={ft === 'datetime' ? 'datetime-local' : 'date'}
                value={String(condition.value ?? '')}
                onChange={(e) => onChange({ ...condition, value: e.target.value })}
                className="h-7 text-xs w-40"
                dir="ltr"
              />
            )
          }
          if (ft === 'time') {
            return (
              <Input
                type="time"
                value={String(condition.value ?? '')}
                onChange={(e) => onChange({ ...condition, value: e.target.value })}
                className="h-7 text-xs w-28"
                dir="ltr"
              />
            )
          }
          return (
            <Input
              value={String(condition.value ?? '')}
              onChange={(e) => onChange({ ...condition, value: e.target.value })}
              placeholder="مقدار"
              className="h-7 text-xs w-28"
              dir="auto"
            />
          )
        })()
      )}

      {/* Pattern operator: regex textbox (kept separate from the type-aware
          branch above because the operand is always a regex string, not a
          value of the source field's type). */}
      {needsValue && !isOptionOp && !isCountOp && isPatternOp && !condition.compare_to_field_key && (
        <Input
          value={String(condition.value ?? '')}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          placeholder="^[0-9]+$"
          className="h-7 text-xs w-32 font-mono"
          dir="ltr"
        />
      )}

      {/* Compare-to-field selector (only for operators where it makes sense) */}
      {canCompareToField && (
        <Select
          value={condition.compare_to_field_key ?? '__literal__'}
          onValueChange={(v) =>
            onChange({
              ...condition,
              compare_to_field_key: v === '__literal__' ? undefined : v,
              value: v === '__literal__' ? condition.value : undefined,
            })
          }
        >
          <SelectTrigger className="h-7 text-xs w-36">
            <SelectValue placeholder="یا مقایسه با فیلد" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__literal__">مقدار ثابت</SelectItem>
            {fields
              .filter((f) => f.field_key !== condition.field_key)
              .map((f) => (
                <SelectItem key={f.field_key} value={f.field_key}>
                  {f.label.fa || f.field_key}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}

function ConditionNode({
  condition,
  fields,
  onChange,
  onRemove,
  depth = 0,
}: {
  condition: ConditionalExpression
  fields: FieldDefinition[]
  onChange: (updated: ConditionalExpression) => void
  onRemove?: () => void
  depth?: number
}) {
  // Declared before the `comparison` early return, not inside the and/or branch
  // below: a hook that only runs for some condition types makes this component's
  // hook count depend on its props, and React matches state by call order. A
  // node switching between comparison and group at the same tree position would
  // hand this state to the wrong component.
  const [groupCollapsed, setGroupCollapsed] = useState(false)

  if (condition.type === 'comparison') {
    return (
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <ComparisonEditor
            condition={condition}
            fields={fields}
            onChange={onChange}
          />
        </div>
        {onRemove && (
          <button onClick={onRemove} className="text-muted-foreground hover:text-destructive mt-1 shrink-0">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    )
  }

  if (condition.type === 'not') {
    return (
      <div className={cn('border border-orange-500/30 rounded-lg p-3 space-y-2', depth > 0 && 'bg-orange-500/5')}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium text-orange-400 bg-orange-500/10 px-1.5 py-0.5 rounded">نه</span>
          <span className="text-[10px] text-muted-foreground">شرط زیر را نقض کن</span>
          {onRemove && (
            <button onClick={onRemove} className="text-muted-foreground hover:text-destructive ms-auto">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="ps-3 border-s border-orange-500/20">
          <ConditionNode
            condition={condition.child}
            fields={fields}
            onChange={(updated) => onChange({ ...condition, child: updated })}
            depth={depth + 1}
          />
        </div>
      </div>
    )
  }

  if (condition.type === 'and' || condition.type === 'or') {

    const addChild = () => {
      const newComp: ComparisonCondition = {
        type: 'comparison',
        field_key: fields[0]?.field_key ?? '',
        operator: 'is_true',
      }
      onChange({ ...condition, children: [...condition.children, newComp] })
    }

    const updateChild = (idx: number, updated: ConditionalExpression) => {
      const next = [...condition.children]
      next[idx] = updated
      onChange({ ...condition, children: next })
    }

    const removeChild = (idx: number) => {
      onChange({ ...condition, children: condition.children.filter((_, i) => i !== idx) })
    }

    return (
      <div className={cn('border rounded-lg p-3 space-y-2', depth > 0 && 'border-border/60 bg-muted/20')}>
        <div className="flex items-center gap-2">
          <Select
            value={condition.type}
            onValueChange={(v) => onChange({ ...condition, type: v as 'and' | 'or' })}
          >
            <SelectTrigger className="h-6 w-16 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="and">و</SelectItem>
              <SelectItem value="or">یا</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[10px] text-muted-foreground">
            شروط زیر را ترکیب کن
            {groupCollapsed && (
              <span className="ms-1 text-muted-foreground/60">({condition.children.length} شرط)</span>
            )}
          </span>
          <button
            onClick={() => setGroupCollapsed((v) => !v)}
            className="text-muted-foreground hover:text-foreground ms-auto"
            title={groupCollapsed ? 'باز کردن گروه' : 'جمع کردن گروه'}
          >
            {groupCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
          {onRemove && (
            <button onClick={onRemove} className="text-muted-foreground hover:text-destructive">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {!groupCollapsed && condition.children.map((child, idx) => (
          <div key={idx} className="ps-3 border-s border-border">
            <ConditionNode
              condition={child}
              fields={fields}
              onChange={(u) => updateChild(idx, u)}
              onRemove={condition.children.length > 1 ? () => removeChild(idx) : undefined}
              depth={depth + 1}
            />
          </div>
        ))}

        {!groupCollapsed && <div className="flex gap-3 pt-1">
          <button
            onClick={addChild}
            className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            شرط
          </button>
          <button
            onClick={() => {
              const group: ConditionalExpression = {
                type: 'and',
                children: [{ type: 'comparison', field_key: fields[0]?.field_key ?? '', operator: 'is_true' }],
              }
              onChange({ ...condition, children: [...condition.children, group] })
            }}
            className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            گروه
          </button>
          <button
            onClick={() => {
              const notGroup: ConditionalExpression = {
                type: 'not',
                child: { type: 'comparison', field_key: fields[0]?.field_key ?? '', operator: 'is_true' },
              }
              onChange({ ...condition, children: [...condition.children, notGroup] })
            }}
            className="text-[10px] text-orange-400/70 hover:text-orange-400 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            نه
          </button>
        </div>}
      </div>
    )
  }

  return null
}

// ─── Action editor ────────────────────────────────────────────────────────────

function ActionRow({
  action,
  fields,
  onChange,
  onRemove,
}: {
  action: ConditionalAction
  fields: FieldDefinition[]
  onChange: (updated: ConditionalAction) => void
  onRemove: () => void
}) {
  const targetField = fields.find((f) => f.field_key === action.target_field_key)
  const targetOptions = (targetField?.type_config?.options as SelectOption[] | undefined) ?? []
  const selectedOptionKeys = (action.param as string[] | undefined) ?? []

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={action.type}
          onValueChange={(v) => onChange({ ...action, type: v as ConditionalAction['type'], param: undefined })}
        >
          <SelectTrigger className="h-7 text-xs w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={action.target_field_key}
          onValueChange={(v) => onChange({ ...action, target_field_key: v, param: undefined })}
        >
          <SelectTrigger className="h-7 text-xs w-40">
            <SelectValue placeholder="فیلد هدف..." />
          </SelectTrigger>
          <SelectContent>
            {fields.map((f) => (
              <SelectItem key={f.field_key} value={f.field_key}>
                {f.label.fa || f.field_key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button onClick={onRemove} className="text-muted-foreground hover:text-destructive">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* set_default_value: text input for the default value */}
      {action.type === 'set_default_value' && (
        <div className="ps-2 flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground shrink-0">مقدار پیش‌فرض:</span>
          <Input
            value={String(action.param ?? '')}
            onChange={(e) => onChange({ ...action, param: e.target.value })}
            placeholder="مقدار..."
            className="h-7 text-xs w-40"
            dir="auto"
          />
        </div>
      )}

      {/* filter_options: checkbox list of the target field's options */}
      {action.type === 'filter_options' && (
        <div className="ps-2 space-y-1">
          <span className="text-[10px] text-muted-foreground">گزینه‌های مجاز:</span>
          {targetOptions.length === 0 ? (
            <p className="text-[10px] text-muted-foreground/60">فیلد هدف گزینه‌ای ندارد</p>
          ) : (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {targetOptions.map((opt) => (
                <button
                  key={opt.option_key}
                  type="button"
                  onClick={() => {
                    const next = selectedOptionKeys.includes(opt.option_key)
                      ? selectedOptionKeys.filter((k) => k !== opt.option_key)
                      : [...selectedOptionKeys, opt.option_key]
                    onChange({ ...action, param: next })
                  }}
                  className={cn(
                    'text-[10px] px-2 py-0.5 rounded border transition-colors',
                    selectedOptionKeys.includes(opt.option_key)
                      ? 'bg-primary/20 text-primary border-primary/40'
                      : 'border-border text-muted-foreground hover:border-primary/30'
                  )}
                >
                  {opt.label.fa || opt.option_key}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Rule editor dialog ───────────────────────────────────────────────────────

function RuleEditorDialog({
  rule,
  fields,
  open,
  onClose,
  onSave,
}: {
  rule: ConditionalRule
  fields: FieldDefinition[]
  open: boolean
  onClose: () => void
  onSave: (updated: ConditionalRule) => void
}) {
  const [local, setLocal] = useState<ConditionalRule>({ ...rule })

  const addAction = () => {
    const newAction: ConditionalAction = {
      type: 'show',
      target_field_key: fields[0]?.field_key ?? '',
    }
    setLocal((r) => ({ ...r, actions: [...r.actions, newAction] }))
  }

  const updateAction = (idx: number, updated: ConditionalAction) => {
    setLocal((r) => {
      const next = [...r.actions]
      next[idx] = updated
      return { ...r, actions: next }
    })
  }

  const removeAction = (idx: number) => {
    setLocal((r) => ({ ...r, actions: r.actions.filter((_, i) => i !== idx) }))
  }

  const wrapInAnd = () => {
    const current = local.condition
    if (current.type === 'and') {
      const newComp: ComparisonCondition = {
        type: 'comparison',
        field_key: fields[0]?.field_key ?? '',
        operator: 'is_true',
      }
      setLocal((r) => ({
        ...r,
        condition: { ...current, children: [...current.children, newComp] },
      }))
    } else {
      setLocal((r) => ({
        ...r,
        condition: { type: 'and', children: [current] },
      }))
    }
  }

  const wrapInNot = () => {
    const current = local.condition
    if (current.type !== 'not') {
      setLocal((r) => ({ ...r, condition: { type: 'not', child: current } }))
    }
  }

  const liveSummary = ruleSummary(local, fields)

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ویرایش قانون شرطی</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Rule name + enabled toggle */}
          <div className="grid grid-cols-[1fr_auto] gap-4 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs">نام قانون</Label>
              <Input
                value={local.description}
                onChange={(e) => setLocal((r) => ({ ...r, description: e.target.value }))}
                placeholder="مثلاً: نمایش وزن اگر بیمار بزرگسال است"
              />
            </div>
            <button
              onClick={() => setLocal((r) => ({ ...r, is_enabled: !r.is_enabled }))}
              className={cn(
                'text-sm flex items-center gap-1.5 h-8',
                local.is_enabled ? 'text-emerald-400' : 'text-muted-foreground',
              )}
            >
              {local.is_enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
              {local.is_enabled ? 'فعال' : 'غیرفعال'}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-3">
            اولویت قانون با ترتیب آن در لیست تعیین می‌شود — برای تغییر اولویت، در لیست با کشیدن جابه‌جا کنید.
          </p>

          {/* Condition (IF) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">شرط (اگر)</Label>
              <div className="flex items-center gap-3">
                <button
                  onClick={wrapInAnd}
                  className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  افزودن شرط
                </button>
                <button
                  onClick={wrapInNot}
                  disabled={local.condition.type === 'not'}
                  className="text-[10px] text-orange-400/70 hover:text-orange-400 flex items-center gap-1 disabled:opacity-30"
                >
                  <Plus className="w-3 h-3" />
                  نقض (NOT)
                </button>
              </div>
            </div>
            <ConditionNode
              condition={local.condition}
              fields={fields}
              onChange={(updated) => setLocal((r) => ({ ...r, condition: updated }))}
            />
          </div>

          {/* Actions (THEN) */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">اکشن‌ها (آنگاه)</Label>
            {local.actions.map((action, idx) => (
              <ActionRow
                key={idx}
                action={action}
                fields={fields}
                onChange={(u) => updateAction(idx, u)}
                onRemove={() => removeAction(idx)}
              />
            ))}
            <button
              onClick={addAction}
              className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
            >
              <Plus className="w-3 h-3" />
              افزودن اکشن
            </button>
          </div>

          {/* Live summary */}
          <div className="bg-muted/30 border border-border rounded-lg p-3">
            <p className="text-[10px] text-muted-foreground mb-1">خلاصه قانون:</p>
            <p className="text-xs text-foreground">{liveSummary}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>انصراف</Button>
          <Button onClick={() => { onSave(local); onClose() }}>ذخیره قانون</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Rules Section ────────────────────────────────────────────────────────────

// ─── Test Panel ───────────────────────────────────────────────────────────────

interface SortableRuleRowProps {
  rule: ConditionalRule
  position: number
  fields: FieldDefinition[]
  hasConflict: boolean
  draggable: boolean
  onToggleEnabled: () => void
  onEdit: () => void
  onDelete: () => void
}

function SortableRuleRow({
  rule, position, fields, hasConflict, draggable,
  onToggleEnabled, onEdit, onDelete,
}: SortableRuleRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.rule_id,
    disabled: !draggable,
  })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }

  const readsFields = Array.from(collectFieldKeys(rule.condition))
    .map((k) => fields.find((f) => f.field_key === k)?.label.fa || k)
  const affectsFields = rule.actions
    .map((a) => fields.find((f) => f.field_key === a.target_field_key)?.label.fa || a.target_field_key)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border rounded-xl p-4 space-y-2 bg-card',
        !rule.is_enabled && 'opacity-50',
        hasConflict && 'border-yellow-500/40 bg-yellow-500/5',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            {...attributes}
            {...listeners}
            disabled={!draggable}
            title={draggable ? 'برای تغییر اولویت بکشید' : 'برای جابه‌جایی ابتدا فیلتر را پاک کنید'}
            className={cn(
              'text-muted-foreground hover:text-foreground shrink-0',
              draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-not-allowed opacity-40',
            )}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono shrink-0">
            #{position.toLocaleString('fa-IR')}
          </span>
          {hasConflict && (
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
          )}
          {!rule.is_enabled && (
            <span className="text-[10px] text-muted-foreground shrink-0">غیرفعال</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onToggleEnabled} className="text-muted-foreground hover:text-foreground">
            {rule.is_enabled
              ? <ToggleRight className="w-4 h-4 text-emerald-400" />
              : <ToggleLeft className="w-4 h-4" />
            }
          </button>
          <button onClick={onEdit} className="text-muted-foreground hover:text-foreground">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="text-muted-foreground hover:text-destructive">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <p className="text-xs text-foreground">
        {rule.description || ruleSummary(rule, fields)}
      </p>
      {rule.description && (
        <p className="text-[10px] text-muted-foreground">{ruleSummary(rule, fields)}</p>
      )}

      <div className="flex flex-wrap gap-3 text-[10px]">
        {readsFields.length > 0 && (
          <span className="text-muted-foreground">
            خواندن: {readsFields.map((f) => (
              <span key={f} className="bg-muted rounded px-1 mx-0.5">{f}</span>
            ))}
          </span>
        )}
        {affectsFields.length > 0 && (
          <span className="text-muted-foreground">
            تأثیر: {affectsFields.map((f) => (
              <span key={f} className="bg-muted rounded px-1 mx-0.5">{f}</span>
            ))}
          </span>
        )}
      </div>
    </div>
  )
}

interface RulesSectionProps {
  definition: FormDefinition
  onAddRule: (rule: ConditionalRule) => void
  onUpdateRule: (rule: ConditionalRule) => void
  onDeleteRule: (ruleId: string) => void
  onReorderRules: (rules: ConditionalRule[]) => void
  /** When set, auto-open the rule editor for this rule_id (single-shot). */
  autoOpenRuleId?: string | null
  onAutoOpenConsumed?: () => void
}

export function RulesSection({
  definition, onAddRule, onUpdateRule, onDeleteRule, onReorderRules,
  autoOpenRuleId, onAutoOpenConsumed,
}: RulesSectionProps) {
  const [editingRule, setEditingRule] = useState<ConditionalRule | null>(null)
  const [pendingRule, setPendingRule] = useState<ConditionalRule | null>(null)
  const [filterField, setFilterField] = useState<string>('')

  // Auto-open editor when the parent requests it (e.g. user clicked a
  // conditional-rule info box from a field's validation tab).
  useEffect(() => {
    if (!autoOpenRuleId) return
    const found = definition.conditional_rules.find((r) => r.rule_id === autoOpenRuleId)
    if (found) setEditingRule(found)
    onAutoOpenConsumed?.()
  }, [autoOpenRuleId, definition.conditional_rules, onAutoOpenConsumed])

  const rules = definition.conditional_rules
  const fields = definition.fields
  const conflicts = detectConflicts(rules, fields)

  const conflictRuleIds = new Set(conflicts.flatMap((c) => [c.ruleId1, c.ruleId2]))

  const filteredRules = filterField
    ? rules.filter((r) => {
        // reads field
        const reads = collectFieldKeys(r.condition)
        // affects field
        const affects = r.actions.map((a) => a.target_field_key)
        return reads.has(filterField) || affects.includes(filterField)
      })
    : rules

  // Array order IS the priority order. Top of list = highest priority.
  const sortedRules = filteredRules
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleRuleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    // Disable drag-reorder while a field filter is applied — the displayed list
    // is a subset; moving items would silently rearrange the hidden ones.
    if (filterField) return
    const oldIdx = rules.findIndex((r) => r.rule_id === active.id)
    const newIdx = rules.findIndex((r) => r.rule_id === over.id)
    if (oldIdx < 0 || newIdx < 0) return
    onReorderRules(arrayMove(rules, oldIdx, newIdx))
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">قوانین شرطی</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            قوانینی که نمایش، الزامی‌بودن، یا مقادیر فیلدها را کنترل می‌کنند
          </p>
          <p className="text-[10px] text-muted-foreground/80 mt-0.5">
            <GripVertical className="w-3 h-3 inline align-text-bottom me-0.5" />
            ترتیب لیست اولویت اجرا را تعیین می‌کند — قوانین بالاتر زودتر اجرا می‌شوند. با کشیدن جابه‌جا کنید.
          </p>
        </div>
        <Button
          size="sm"
          className="gap-1.5"
          disabled={fields.length < 2}
          onClick={() => {
            const newRule: ConditionalRule = {
              rule_id: nanoid(),
              is_enabled: true,
              // Priority is derived from list position on save — placeholder here.
              priority: (rules.length + 1) * 10,
              description: '',
              condition: { type: 'comparison', field_key: fields[0]?.field_key ?? '', operator: 'is_true' },
              actions: [{ type: 'show', target_field_key: fields[1]?.field_key ?? fields[0]?.field_key ?? '' }],
            }
            setPendingRule(newRule)
          }}
        >
          <Plus className="w-4 h-4" />
          قانون جدید
        </Button>
      </div>

      {/* Conflict banner */}
      {conflicts.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 space-y-1">
          <p className="text-xs font-medium text-yellow-400 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            {conflicts.length} تعارض شناسایی شد
          </p>
          {conflicts.map((c, i) => (
            <p key={i} className="text-[10px] text-yellow-300/80">{c.message}</p>
          ))}
        </div>
      )}

      {/* Filter by field */}
      {fields.length > 0 && (
        <div className="flex items-center gap-2">
          <Label className="text-xs shrink-0">فیلتر بر اساس فیلد:</Label>
          <Select value={filterField || '__all__'} onValueChange={(v) => setFilterField(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-7 text-xs w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">همه قوانین</SelectItem>
              {fields.map((f) => (
                <SelectItem key={f.field_key} value={f.field_key}>
                  {f.label.fa || f.field_key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Rule list */}
      {sortedRules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <GitBranch className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">
            {fields.length < 2
              ? 'ابتدا حداقل ۲ فیلد اضافه کنید'
              : 'هنوز قانونی تعریف نشده است'}
          </p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRuleDragEnd}>
          <SortableContext items={sortedRules.map((r) => r.rule_id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {sortedRules.map((rule, idx) => (
                <SortableRuleRow
                  key={rule.rule_id}
                  rule={rule}
                  position={idx + 1}
                  fields={fields}
                  hasConflict={conflictRuleIds.has(rule.rule_id)}
                  draggable={!filterField}
                  onToggleEnabled={() => onUpdateRule({ ...rule, is_enabled: !rule.is_enabled })}
                  onEdit={() => setEditingRule(rule)}
                  onDelete={() => onDeleteRule(rule.rule_id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Test panel removed: use the side-by-side پیش‌نمایش toggle in the header
          to apply these rules against a live form. */}

      {/* Rule editor dialog — editing existing */}
      {editingRule && (
        <RuleEditorDialog
          rule={editingRule}
          fields={fields}
          open={true}
          onClose={() => setEditingRule(null)}
          onSave={(updated) => {
            onUpdateRule(updated)
            setEditingRule(null)
          }}
        />
      )}

      {/* Rule editor dialog — adding new */}
      {pendingRule && (
        <RuleEditorDialog
          rule={pendingRule}
          fields={fields}
          open={true}
          onClose={() => setPendingRule(null)}
          onSave={(completed) => {
            onAddRule(completed)
            setPendingRule(null)
          }}
        />
      )}
    </div>
  )
}

